// Territoria Rankings — definitions + deterministic mock data generator.
// The data architecture allows new categories to be added without touching
// page logic — just append to the relevant tab's `categories` block.

// ============================================================
// 1.  TAB & CATEGORY DEFINITIONS
// ============================================================

// ============================================================
// LIVE GAME DATA CONFIG (Feb 2026+ — territoria.world alpha)
// ============================================================
// These tabs pull from our backend proxy /api/game-rankings/{category}
// instead of the deterministic mock generator below. Metrics come from
// the live upstream (see /app/backend/game_rankings.py VALID_METRICS).

const dmgFmt   = (v) => `${(v ?? 0).toLocaleString("en-US")} dmg`;
const goldFmt  = (v) => `${(v ?? 0).toLocaleString("en-US")}`;
const numFmt   = (v) => (v ?? 0).toLocaleString("en-US");
const lvlFmt   = (v) => `Lv. ${v ?? 0}`;

export const LIVE_TABS = new Set(["citizens", "nations"]);

export const LIVE_CATEGORIES = {
  citizens: [
    {
      group: "Military",
      items: [
        { key: "damages", label: "Damage", metric: "damages", unit: "DMG",  fmt: dmgFmt, emphasized: true },
      ],
    },
    {
      group: "Economy",
      items: [
        { key: "wealth",  label: "Wealth", metric: "wealth",  unit: "gold", fmt: goldFmt },
      ],
    },
    {
      group: "Progression",
      items: [
        { key: "level",   label: "Level",  metric: "level",   unit: "lvl",  fmt: lvlFmt },
      ],
    },
  ],
  nations: [
    {
      group: "Military",
      items: [
        { key: "damages",     label: "Damage",       metric: "damages",     unit: "DMG",     fmt: dmgFmt, emphasized: true },
      ],
    },
    {
      group: "Community",
      items: [
        { key: "population",  label: "Population",   metric: "population",  unit: "citizens", fmt: numFmt },
      ],
    },
    {
      group: "Economy",
      items: [
        { key: "wealth",      label: "Wealth",       metric: "wealth",      unit: "gold",     fmt: goldFmt },
      ],
    },
    {
      group: "Development",
      items: [
        { key: "development", label: "Development",  metric: "development", unit: "pts",      fmt: numFmt },
      ],
    },
    {
      group: "Power",
      items: [
        { key: "region-diff", label: "Region Advantage", metric: "region-diff", unit: "regions", fmt: numFmt },
      ],
    },
  ],
};

export function findLiveCategory(tab, categoryKey) {
  const groups = LIVE_CATEGORIES[tab];
  if (!groups) return null;
  for (const g of groups) for (const it of g.items) if (it.key === categoryKey) return it;
  return null;
}

export function defaultLiveCategory(tab) {
  const groups = LIVE_CATEGORIES[tab];
  if (!groups) return null;
  for (const g of groups) for (const it of g.items) if (it.emphasized) return it.key;
  return groups[0]?.items[0]?.key || null;
}


// ============================================================
// LEGACY MOCK GENERATOR (kept for Organizations / Alliances tabs
// while their live endpoints don't exist yet)
// ============================================================

export const PERIODS = [
  { key: "daily",   label: "Daily" },
  { key: "weekly",  label: "Weekly", emphasized: true },
  { key: "monthly", label: "Monthly" },
  { key: "all",     label: "All Time" },
];

// Each category declares display + formatting + a deterministic seed offset
// so the same (tab, category, period) combo always produces the same data.
const num    = (v) => v.toLocaleString("en-US");
const money  = (v) => `$${v.toLocaleString("en-US")}`;
const pct    = (v) => `${v.toFixed(1)}%`;
const level  = (v) => `Lv. ${v}`;
const dmg    = (v) => `${v.toLocaleString("en-US")} dmg`;

export const TABS = [
  {
    key: "citizens",
    label: "Citizens",
    description: "Individual players ranked across the empire.",
    entity: "citizen",
    groups: [
      {
        group: "Military",
        items: [
          { key: "weekly-damage", label: "Weekly Damage", fmt: dmg,   max: 8_400_000,  unit: "DMG", emphasized: true },
          { key: "total-damage",  label: "Damage",  fmt: dmg,   max: 142_000_000, unit: "DMG", stock: true },
          { key: "kills",         label: "Kills",         fmt: num,   max: 24_500,      unit: "kills" },
          { key: "bounty-earned", label: "Bounty Earned", fmt: money, max: 18_400_000,  unit: "$" },
        ],
      },
      {
        group: "Progression",
        items: [
          { key: "level",      label: "Level",      fmt: level, max: 312, unit: "Lv.", stock: true },
          { key: "experience", label: "Experience", fmt: num,   max: 24_800_000, unit: "XP", stock: true },
          { key: "prestige",   label: "Prestige",   fmt: num,   max: 47, unit: "P", stock: true },
        ],
      },
      {
        group: "Economy",
        items: [
          { key: "wealth",           label: "Wealth",           fmt: money, max: 84_000_000, unit: "$", stock: true },
          { key: "production-value", label: "Production Value", fmt: money, max: 28_400_000, unit: "$" },
          { key: "trade-volume",     label: "Trade Volume",     fmt: money, max: 142_000_000, unit: "$" },
        ],
      },
      {
        group: "Community",
        items: [
          { key: "referrals",    label: "Referrals",    fmt: num, max: 240, unit: "refs" },
          { key: "achievements", label: "Achievements", fmt: num, max: 42, unit: "badges", stock: true },
        ],
      },
    ],
  },

  {
    key: "nations",
    label: "Nations",
    description: "Sovereign powers ranked by global influence.",
    entity: "nation",
    groups: [
      {
        group: "Military",
        items: [
          { key: "weekly-damage",    label: "Weekly Damage",            fmt: dmg, max: 84_000_000,  unit: "DMG", emphasized: true },
          { key: "total-damage",     label: "Damage",             fmt: dmg, max: 1_240_000_000, unit: "DMG", stock: true },
          { key: "weekly-dmg-per-c", label: "Weekly Damage Per Citizen", fmt: dmg, max: 420_000,    unit: "DMG/C" },
        ],
      },
      {
        group: "Development",
        items: [
          { key: "development-level", label: "Development Level", fmt: level, max: 95, unit: "Lv.", stock: true },
          { key: "active-population", label: "Active Population", fmt: num,   max: 12_000, unit: "citizens", stock: true },
        ],
      },
      {
        group: "Economy",
        items: [
          { key: "wealth",            label: "Wealth",            fmt: money, max: 1_840_000_000, unit: "$", stock: true },
          { key: "production-output", label: "Production Output", fmt: num,   max: 84_000_000, unit: "units" },
        ],
      },
    ],
  },

  {
    key: "organizations",
    label: "Organizations",
    description: "Corporations, syndicates, and player-run companies.",
    entity: "organization",
    groups: [
      {
        group: "Performance",
        items: [
          { key: "wealth",            label: "Wealth",            fmt: money, max: 240_000_000, unit: "$", stock: true, emphasized: true },
          { key: "production-output", label: "Production Output", fmt: num,   max: 18_400_000, unit: "units" },
          { key: "active-members",    label: "Active Members",    fmt: num,   max: 420, unit: "members", stock: true },
        ],
      },
    ],
  },

  {
    key: "alliances",
    label: "Alliances",
    description: "Coalitions of nations shaping the world order.",
    entity: "alliance",
    groups: [
      {
        group: "Power",
        items: [
          { key: "weekly-damage",   label: "Weekly Damage",   fmt: dmg,   max: 320_000_000, unit: "DMG", emphasized: true },
          { key: "total-damage",    label: "Total Damage",    fmt: dmg,   max: 4_800_000_000, unit: "DMG", stock: true },
          { key: "development",     label: "Development",     fmt: level, max: 88, unit: "Lv.", stock: true },
          { key: "active-population", label: "Active Population", fmt: num, max: 48_000, unit: "members", stock: true },
          { key: "member-count",    label: "Member Count",    fmt: num, max: 47, unit: "nations", stock: true },
        ],
      },
    ],
  },
];

// ============================================================
// 2.  ENTITY POOLS — names + nation flags
// ============================================================

// FlagCDN uses ISO 3166-1 alpha-2 country codes.
const NATIONS = [
  { name: "United States",   code: "us" },
  { name: "Russia",           code: "ru" },
  { name: "China",            code: "cn" },
  { name: "Germany",          code: "de" },
  { name: "France",           code: "fr" },
  { name: "United Kingdom",   code: "gb" },
  { name: "Japan",            code: "jp" },
  { name: "Brazil",           code: "br" },
  { name: "India",            code: "in" },
  { name: "Turkey",           code: "tr" },
  { name: "Canada",           code: "ca" },
  { name: "Australia",        code: "au" },
  { name: "Italy",            code: "it" },
  { name: "Spain",            code: "es" },
  { name: "Mexico",           code: "mx" },
  { name: "South Korea",      code: "kr" },
  { name: "Argentina",        code: "ar" },
  { name: "Poland",           code: "pl" },
  { name: "Iran",             code: "ir" },
  { name: "Egypt",            code: "eg" },
  { name: "Saudi Arabia",     code: "sa" },
  { name: "Sweden",           code: "se" },
  { name: "Indonesia",        code: "id" },
  { name: "South Africa",     code: "za" },
  { name: "Netherlands",      code: "nl" },
  { name: "Norway",           code: "no" },
  { name: "Israel",           code: "il" },
  { name: "Ukraine",          code: "ua" },
  { name: "Vietnam",          code: "vn" },
  { name: "Pakistan",         code: "pk" },
];

const CITIZENS = [
  "Voronoff", "BlackHawk", "Imperator", "RedFox", "Ironclad",
  "Sovereign", "Kingmaker", "Pyrrhus", "Halberd", "Praetor",
  "Saber", "NightOwl", "Crimson", "Vanguard", "GlassCannon",
  "Phantom", "Tactician", "Bastion", "Marauder", "Steelborn",
  "Reaper", "Centurion", "Caelum", "Valkyrie", "Stormbreaker",
  "Warden", "OmenX", "Cipher", "Apex", "Eclipse",
  "Nyx", "Helios", "Atlas", "Orion", "Hadrian",
  "Tempest", "Frost", "Ronin", "Sentinel", "Mercury",
];

const ORGANIZATIONS = [
  "Hyperion Mining Co.",   "Sable Logistics",       "Iron Crescent Arms",
  "Nordvik Heavy Industry", "Crimson Trade Guild",   "Apex Munitions",
  "Black Veil Syndicate",   "Atlas Reserve Bank",    "Vega Microsteel",
  "Helios Refinery",         "Thorn & Vance Holdings", "Polaris Cartel",
  "Wildcat Mercenaries",     "Saturn Heavy Yards",     "Obsidian Trust",
  "Krieger-Vance Trading",   "Meridian Imports",       "Ashfield Pharma",
  "Talon Aerospace",         "Nightshade Logistics",   "Ferrum Industrial",
  "Onyx Reclamation",        "Lex Aurora Capital",     "Stormcrow Munitions",
];

const ALLIANCES = [
  "Pacific Concord",       "Northern Pact",          "League of Free Cities",
  "Crimson Coalition",     "Atlantic Charter",        "Iron Curtain Bloc",
  "Equatorial Union",      "Silk Road Accord",        "Sovereignty Bloc",
  "Trident Alliance",       "Phoenix Brotherhood",    "Order of the Lion",
  "Black Sun Pact",         "Free World Initiative",  "Bastion League",
];

// ============================================================
// 3.  DETERMINISTIC PRNG (LCG) — so same inputs produce same data
// ============================================================

function hashString(s) {
  let h = 2166136261 >>> 0;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

function makeRng(seed) {
  let state = seed >>> 0;
  return () => {
    state = (state * 1664525 + 1013904223) >>> 0;
    return state / 0x100000000;
  };
}

// ============================================================
// 4.  ROW GENERATOR
// ============================================================

function pickEntityPool(tab) {
  if (tab === "citizens") return { names: CITIZENS, withFlag: true };
  if (tab === "nations") return { names: NATIONS.map(n => n.name), withFlag: false /* nation IS the flag */ };
  if (tab === "organizations") return { names: ORGANIZATIONS, withFlag: true };
  if (tab === "alliances") return { names: ALLIANCES, withFlag: false };
  return { names: [], withFlag: false };
}

export function buildLeaderboard({ tab, category, period, count = 25 }) {
  const cat = findCategory(tab, category);
  if (!cat) return [];

  const seedKey = `${tab}::${category}::${period}`;
  const rng = makeRng(hashString(seedKey));

  // Period multipliers:
  //   • Flow metrics (damage gained / production made / kills earned in window)
  //     scale linearly — small window = small numbers, all-time = huge.
  //   • Stock metrics (current wealth / population / level / lifetime total
  //     damage) are snapshots, so the period barely affects them.
  //     All-Time on a stock = peak ever, slight bump.
  const FLOW_MUL  = { daily: 0.18, weekly: 1.0,  monthly: 3.6, all: 18.0 };
  const STOCK_MUL = { daily: 0.96, weekly: 1.0,  monthly: 1.02, all: 1.12 };
  const periodMul = (cat.stock ? STOCK_MUL : FLOW_MUL)[period] ?? 1.0;

  const pool = pickEntityPool(tab);
  // shuffle names deterministically
  const names = [...pool.names];
  for (let i = names.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [names[i], names[j]] = [names[j], names[i]];
  }

  // assign nations to citizens / orgs deterministically
  const nationRng = makeRng(hashString(seedKey + "::nation"));
  const flags = [...NATIONS];
  for (let i = flags.length - 1; i > 0; i--) {
    const j = Math.floor(nationRng() * (i + 1));
    [flags[i], flags[j]] = [flags[j], flags[i]];
  }

  // generate raw scores via decaying random walk, sort desc
  const raw = [];
  let top = cat.max * (0.85 + rng() * 0.15) * periodMul;
  for (let i = 0; i < count; i++) {
    const decay = 0.94 + rng() * 0.04;
    top *= decay;
    raw.push(top * (0.92 + rng() * 0.16));
  }
  raw.sort((a, b) => b - a);

  // build entries
  const entries = [];
  for (let i = 0; i < Math.min(count, names.length); i++) {
    const name = names[i];
    let nation = null;
    if (tab === "nations") {
      // for nations, the entity itself has a flag
      const nationDef = NATIONS.find(n => n.name === name);
      nation = nationDef || null;
    } else if (pool.withFlag) {
      nation = flags[i % flags.length];
    }
    // movement: deterministic but biased small numbers
    const moveRng = makeRng(hashString(seedKey + "::move::" + i));
    const moveRoll = moveRng();
    let change;
    if (moveRoll < 0.18) change = 0;
    else {
      const sign = moveRng() < 0.5 ? -1 : 1;
      const mag = 1 + Math.floor(moveRng() * 8);
      change = sign * mag;
    }
    const score = cat.key === "level" || cat.key === "development-level" || cat.key === "development" || cat.key === "prestige" || cat.key === "achievements" || cat.key === "member-count"
      ? Math.max(1, Math.round(raw[i]))
      : Math.round(raw[i]);

    entries.push({
      rank: i + 1,
      name,
      nation, // {name, code} or null
      score,
      change, // signed integer (rank delta)
    });
  }
  return entries;
}

export function findCategory(tab, categoryKey) {
  const t = TABS.find(t => t.key === tab);
  if (!t) return null;
  for (const g of t.groups) {
    for (const it of g.items) {
      if (it.key === categoryKey) return it;
    }
  }
  return null;
}

export function defaultCategory(tab) {
  const t = TABS.find(t => t.key === tab);
  if (!t) return null;
  // pick first emphasized category, fallback to first item
  for (const g of t.groups) {
    for (const it of g.items) {
      if (it.emphasized) return it.key;
    }
  }
  return t.groups[0]?.items[0]?.key || null;
}

export function flagUrl(code) {
  if (!code) return null;
  return `https://flagcdn.com/w40/${String(code).toLowerCase()}.png`;
}
